import PropTypes from 'prop-types'

/**
 * Properties of an SvgIcon component.
 *
 * @typedef {Object} SvgIconProps
 * @property {string} [className] - Additional CSS class added to the SVG element.
 * @property {ReactElement} [children] - All supported SVG child elements (https://www.w3.org/TR/SVG11/).
 * @property {boolean} [fillParent] - Force icon height and width to 100% (overrides height and width properties).
 * @property {boolean} [flipHorizontal] - Horizontally flip (mirror) the icon.
 * @property {boolean} [flipVertical] - Vertically flip (mirror) the icon.
 * @property {number} [height] - Height of the icon. If not provided, either the given width or a default size is used.
 * @property {boolean} [inheritColor] - Use parent element's color as fill color for the first path element.
 * @property {boolean} [keepPathColor] - Do NOT set default color to the first path element.
 * @property {boolean} [namespace] - The standard XML namespace is added to the SVG element.
 * @property {Function} [onBlur] - Callback passed to SVG element - important if SvgIcon is wrapped in a
 *                                 {@link Tooltip} element.
 * @property {Function} [onClick] - Callback passed to SVG element - important if SvgIcon is wrapped in a
 *                                  {@link Tooltip} element.
 * @property {Function} [onFocus] - Callback passed to SVG element - important if SvgIcon is wrapped in a
 *                                  {@link Tooltip} element.
 * @property {Function} [onMouseDown] - Callback passed to SVG element - important if SvgIcon is wrapped in a
 *                                      {@link Tooltip} element.
 * @property {Function} [onMouseEnter] - Callback passed to SVG element - important if SvgIcon is wrapped in a
 *                                       {@link Tooltip} element.
 * @property {Function} [onMouseLeave] - Callback passed to SVG element - important if SvgIcon is wrapped in a
 *                                       {@link Tooltip} element.
 * @property {Function} [onTouchStart] - Callback passed to SVG element - important if SvgIcon is wrapped in a
 *                                       {@link Tooltip} element.
 * @property {number} [rotateDegrees] - Number of degrees for icon rotation.
 * @property {Object} [style] - Additional (inline) CSS style(s) added to the SVG element.
 * @property {string} [viewBox] - Direct `viewBox` attribute value for the SVG element containing 4 space separated
 *                                numbers, e.g. "0 0 256 128" (overrides viewBoxSize).
 * @property {number} [viewBoxSize] - Size of SVG view box. Defaults to 24. Creates a square view box attribute value
 *                                    of '0 0 24 24' by default.
 * @property {number} [width] - Width of the icon. If not provided, either given height or a default size is used.
 */
const svgIconPropTypeObject = {
    children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    className: PropTypes.string,
    fillParent: PropTypes.bool,
    flipHorizontal: PropTypes.bool,
    flipVertical: PropTypes.bool,
    height: PropTypes.number,
    inheritColor: PropTypes.bool,
    keepPathColor: PropTypes.bool,
    namespace: PropTypes.bool,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onMouseDown: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onTouchStart: PropTypes.func,
    rotateDegrees: PropTypes.number,
    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    viewBox: PropTypes.string,
    viewBoxSize: PropTypes.number,
    width: PropTypes.number
}

const svgIconPropType = PropTypes.shape(svgIconPropTypeObject)

export { svgIconPropType, svgIconPropTypeObject }
